/**
 * Dialog zum Bearbeiten der Default-Fragen der Teaser-Analyse.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import type { TeaserDefaultPrompt } from '../../types/teaser';
import { teaserErrorMessage, useSaveTeaserDefaults } from '../../hooks/useTeaser';

interface Props {
  open: boolean;
  onClose: () => void;
  /** undefined, solange die Default-Fragen nicht erfolgreich geladen wurden */
  defaults: TeaserDefaultPrompt[] | undefined;
  defaultsLoading: boolean;
  defaultsError: boolean;
  tenantId: number | undefined;
}

interface Row {
  key: number;
  id?: number;
  content: string;
}

let rowCounter = 0;

const TeaserDefaultPromptsDialog: React.FC<Props> = ({
  open,
  onClose,
  defaults,
  defaultsLoading,
  defaultsError,
  tenantId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<Row[]>([]);
  const [initializedFor, setInitializedFor] = useState<number | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveTeaserDefaults(tenantId);

  // Lokalen Zustand erst initialisieren, wenn die Daten geladen sind (und je Mandant neu)
  useEffect(() => {
    if (!open) {
      setInitializedFor(undefined);
      return;
    }
    if (defaults !== undefined && initializedFor !== (tenantId ?? null)) {
      setRows(
        [...defaults]
          .sort((a, b) => a.position - b.position)
          .map((d) => ({ key: ++rowCounter, id: d.id, content: d.content }))
      );
      setError(null);
      setInitializedFor(tenantId ?? null);
    }
  }, [open, defaults, tenantId, initializedFor]);

  const ready = defaults !== undefined && initializedFor === (tenantId ?? null);

  const move = (index: number, delta: number) => {
    setRows((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    const prompts = rows
      .filter((r) => r.content.trim())
      .map((r) => (r.id != null ? { id: r.id, content: r.content.trim() } : { content: r.content.trim() }));
    try {
      await save.mutateAsync(prompts);
      onClose();
    } catch (e) {
      setError(teaserErrorMessage(e, 'Speichern fehlgeschlagen'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="md" fullWidth>
      <DialogTitle>Default-Fragen</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Platzhalter in eckigen Klammern, z. B. [Ort]. Änderungen gelten nur für neue Analysen.
        </Alert>
        {defaultsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress />
          </Box>
        )}
        {defaultsError && defaults === undefined && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Die Default-Fragen konnten nicht geladen werden. Bearbeiten und Speichern ist erst
            nach erfolgreichem Laden möglich.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {ready && (
        <Stack spacing={1.5}>
          {rows.length === 0 && (
            <Typography color="text.secondary">Noch keine Default-Fragen angelegt.</Typography>
          )}
          {rows.map((row, i) => (
            <Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={`Frage ${i + 1}`}
                value={row.content}
                inputProps={{ maxLength: 1000 }}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) => (r.key === row.key ? { ...r, content: e.target.value } : r))
                  )
                }
              />
              <Stack>
                <Tooltip title="Nach oben">
                  <span>
                    <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Nach unten">
                  <span>
                    <IconButton
                      size="small"
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Löschen">
                  <IconButton
                    size="small"
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>
        )}
        <Button
          startIcon={<AddIcon />}
          disabled={!ready}
          sx={{ mt: 2 }}
          onClick={() => setRows((prev) => [...prev, { key: ++rowCounter, content: '' }])}
        >
          Frage
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={save.isPending || !ready}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeaserDefaultPromptsDialog;
